import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useModulosAtivos } from '../hooks/useModulosAtivos';
import { AtendimentosCliente } from './whatsapp/HistoricoAtendimento';
import { clientsAPI, partnersAPI, localizacaoAPI, projectsAPI, filesAPI, usersAPI, instituicoesAPI } from '../lib/api';
import { filtrarUsuariosAtribuicao } from '../lib/users';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, MessageCircle, Eye, EyeOff, FolderOpen, CheckCircle, Clock, XCircle, FileText, Upload, Download, Trash2, UserPlus, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import { useLimiteUpload } from '../lib/uploadLimit';

const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const formatCNPJ = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

const formatCPFCNPJ = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 11) {
    return formatCPF(value);
  }
  return formatCNPJ(value);
};

const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

const ClientForm = () => {
  const { whatsappAtivo } = useModulosAtivos();
  const limiteUpload = useLimiteUpload();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const { user } = useAuth();
  const isAdmin = user?.role === 'master' || user?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [estados, setEstados] = useState([]);
  const [showSenhaGov, setShowSenhaGov] = useState(false);
  const [clientProjects, setClientProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [expandedClientProject, setExpandedClientProject] = useState(null);
  const [instituicoes, setInstituicoes] = useState([]);
  
  // Documentos do Cliente
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Arquivos de Projeto (pasta separada)
  const [projectFiles, setProjectFiles] = useState([]);
  const [uploadingProject, setUploadingProject] = useState(false);
  const [selectedProjectForUpload, setSelectedProjectForUpload] = useState('');
  
  // Transferência
  const [transferDialog, setTransferDialog] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState('');
  const [transferindo, setTransferindo] = useState(false);
  
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf: '',
    endereco: '',
    bloco: '',
    complemento: '',
    area_total: '',
    area_construida: '',
    telefone: '',
    data_nascimento: '',
    parceiro_id: '',
    estado: '',
    cidade: '',
    usuario_gov: '',
    senha_gov: '',
    usuarios_gov: [],
    criado_por_nome: '',
    banco: '',
    agencia: '',
    conta: '',
  });

  useEffect(() => {
    const init = async () => {
      try {
        // Carregar todos os dados de referência em paralelo
        const [estadosRes, partnersRes, instituicoesRes] = await Promise.all([
          localizacaoAPI.getEstados().catch(() => ({ data: [] })),
          partnersAPI.list().catch(() => ({ data: [] })),
          instituicoesAPI.list().catch(() => ({ data: [] }))
        ]);
        
        setEstados(estadosRes.data || []);
        setPartners((partnersRes.data || []).filter(p => p.ativo));
        setInstituicoes(instituicoesRes.data || []);
        
        // Só depois de carregar os dados de referência, carrega o cliente
        if (isEditing) {
          await loadClient(instituicoesRes.data || []);
        }
      } catch (error) {
        console.error('Erro ao inicializar formulário:', error);
      }
    };
    init();
  }, [id]);

  const loadInstituicoes = async () => {
    try {
      const response = await instituicoesAPI.list();
      setInstituicoes(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar instituições financeiras');
      return [];
    }
  };

  const loadEstados = async () => {
    try {
      const response = await localizacaoAPI.getEstados();
      setEstados(response.data);
    } catch (error) {
      console.error('Erro ao carregar estados');
    }
  };

  const loadPartners = async () => {
    try {
      const response = await partnersAPI.list();
      setPartners(response.data.filter(p => p.ativo));
    } catch (error) {
      console.error('Erro ao carregar parceiros');
    }
  };

  const loadClient = async (instituicoesList = []) => {
    try {
      setLoading(true);
      const response = await clientsAPI.get(id);
      const client = response.data;
      
      // Encontrar o banco correto (case-insensitive)
      let bancoValue = client.banco || '';
      if (bancoValue && instituicoesList.length > 0) {
        const bancoMatch = instituicoesList.find(
          i => i.nome.toUpperCase() === bancoValue.toUpperCase()
        );
        if (bancoMatch) {
          bancoValue = bancoMatch.nome;
        }
      }
      
      // Seta o formData
      setFormData({
        nome_completo: client.nome_completo || '',
        cpf: formatCPFCNPJ(client.cpf || ''),
        endereco: client.endereco || '',
        bloco: client.bloco || '',
        complemento: client.complemento || '',
        area_total: client.area_total || '',
        area_construida: client.area_construida || '',
        telefone: formatPhone(client.telefone || ''),
        data_nascimento: client.data_nascimento || '',
        parceiro_id: client.parceiro_id || '',
        estado: client.estado || '',
        cidade: client.cidade || '',
        usuario_gov: client.usuario_gov || '',
        senha_gov: client.senha_gov || '',
        usuarios_gov: Array.isArray(client.usuarios_gov) ? client.usuarios_gov : [],
        criado_por_nome: client.criado_por_nome || '',
        banco: bancoValue,
        agencia: client.agencia || '',
        conta: client.conta || '',
      });
      
      // Carregar projetos e documentos do cliente
      loadClientProjects();
      loadFiles();
      loadProjectFiles();
    } catch (error) {
      toast.error('Erro ao carregar cliente');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadClientProjects = async () => {
    if (!id) return;
    try {
      setLoadingProjects(true);
      const response = await projectsAPI.listByClient(id);
      setClientProjects(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar projetos do cliente:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Funções de Documentos
  const loadFiles = async () => {
    if (!id) return;
    try {
      const response = await filesAPI.list(id);
      const filesData = Array.isArray(response.data) ? response.data : (response.data.files || []);
      setFiles(filesData);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const invalidFiles = selectedFiles.filter(f => f.size > limiteUpload.bytes);
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} arquivo(s) excedem o limite de ${limiteUpload.mb} MB`);
      return;
    }

    try {
      setUploading(true);
      let successCount = 0;
      for (const file of selectedFiles) {
        try {
          await filesAPI.upload(id, file);
          successCount++;
        } catch (error) {
          console.error(`Erro ao enviar ${file.name}:`, error);
        }
      }
      if (successCount > 0) {
        toast.success(`${successCount} arquivo(s) enviado(s)`);
      }
      await loadFiles();
      e.target.value = '';
    } catch (error) {
      toast.error('Erro ao enviar arquivos');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      // Usar abordagem direta com link para evitar problemas com arquivos grandes
      const token = localStorage.getItem('agrolink_token');
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}/${encodeURIComponent(filename)}`;
      
      // Criar um link temporário e simular clique
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      
      // Adicionar token via fetch para verificar autenticação
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteFile = async (filename) => {
    if (!window.confirm(`Excluir ${filename}?`)) return;
    try {
      await filesAPI.delete(id, filename);
      toast.success('Arquivo excluído');
      await loadFiles();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  // Funções de Arquivos de Projeto (pasta projeto_[projeto_id])
  const loadProjectFiles = async () => {
    if (!id) return;
    try {
      // Primeiro, buscar todos os projetos do cliente
      const projectsResponse = await projectsAPI.listByClient(id);
      const projects = projectsResponse.data || [];
      
      // Se tiver apenas 1 projeto, selecionar automaticamente para upload
      if (projects.length === 1) {
        setSelectedProjectForUpload(projects[0].id);
      } else if (projects.length > 1 && !selectedProjectForUpload) {
        setSelectedProjectForUpload(projects[0].id);
      }
      
      // Carregar arquivos de todos os projetos do cliente
      let allProjectFiles = [];
      for (const proj of projects) {
        try {
          const response = await filesAPI.list(`projeto_${proj.id}`);
          const filesData = Array.isArray(response.data) ? response.data : (response.data.files || []);
          // Adicionar informação do projeto a cada arquivo
          const filesWithProject = filesData.map(f => ({
            ...f,
            projeto_nome: proj.tipo_projeto_nome || 'Projeto',
            projeto_id: proj.id
          }));
          allProjectFiles = [...allProjectFiles, ...filesWithProject];
        } catch (err) {
          // Continuar mesmo se um projeto não tiver arquivos
        }
      }
      
      // Ordenar por data (mais recente primeiro)
      allProjectFiles.sort((a, b) => {
        const dateA = a.uploaded_at ? new Date(a.uploaded_at) : new Date(0);
        const dateB = b.uploaded_at ? new Date(b.uploaded_at) : new Date(0);
        return dateB - dateA;
      });
      
      setProjectFiles(allProjectFiles);
    } catch (error) {
      console.error('Erro ao carregar arquivos de projeto:', error);
    }
  };

  const handleProjectFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Verificar se tem projeto selecionado
    if (!selectedProjectForUpload) {
      toast.error('Selecione um projeto para fazer upload');
      return;
    }

    const invalidFiles = selectedFiles.filter(f => f.size > limiteUpload.bytes);
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} arquivo(s) excedem o limite de ${limiteUpload.mb} MB`);
      return;
    }

    try {
      setUploadingProject(true);
      let successCount = 0;
      for (const file of selectedFiles) {
        try {
          await filesAPI.upload(`projeto_${selectedProjectForUpload}`, file);
          successCount++;
        } catch (error) {
          console.error(`Erro ao enviar ${file.name}:`, error);
        }
      }
      if (successCount > 0) {
        toast.success(`${successCount} arquivo(s) de projeto enviado(s)`);
      }
      await loadProjectFiles();
      e.target.value = '';
    } catch (error) {
      toast.error('Erro ao enviar arquivos');
    } finally {
      setUploadingProject(false);
    }
  };

  const handleProjectDownload = async (filename, projetoId) => {
    try {
      const token = localStorage.getItem('agrolink_token');
      const folderId = `projeto_${projetoId}`;
      const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${folderId}/${encodeURIComponent(filename)}`;
      
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDeleteProjectFile = async (filename, projetoId) => {
    if (!window.confirm(`Excluir ${filename}?`)) return;
    try {
      await filesAPI.delete(`projeto_${projetoId}`, filename);
      toast.success('Arquivo excluído');
      await loadProjectFiles();
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  // Funções de Transferência
  const openTransferDialog = async () => {
    try {
      const response = await usersAPI.list();
      // Admin pode transferir para si mesmo também
      setUsuarios(response.data.filter(u => u.ativo));
      setTransferDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const handleTransferir = async () => {
    if (!selectedUsuario) {
      toast.error('Selecione um usuário');
      return;
    }
    try {
      setTransferindo(true);
      await clientsAPI.transferir(id, selectedUsuario);
      toast.success('Cliente transferido com sucesso');
      setTransferDialog(false);
      loadClient();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir cliente');
    } finally {
      setTransferindo(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === 'cpf') {
      value = formatCPFCNPJ(value);
    } else if (field === 'telefone') {
      value = formatPhone(value);
    } else if (field === 'estado') {
      // Se "none" foi selecionado, limpar estado
      const estadoValue = value === 'none' ? '' : value;
      setFormData(prev => ({ ...prev, estado: estadoValue }));
      return;
    } else if (field === 'parceiro_id') {
      // Se "none" foi selecionado, limpar parceiro
      value = value === 'none' ? '' : value;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openWhatsApp = () => {
    const phone = formData.telefone.replace(/\D/g, '');
    if (phone.length >= 10) {
      const formattedPhone = phone.startsWith('55') ? phone : `55${phone}`;
      window.open(`https://wa.me/${formattedPhone}`, '_blank');
    } else {
      toast.error('Telefone inválido para WhatsApp');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome_completo || !formData.telefone) {
      toast.error('Preencha os campos obrigatórios: Nome e Telefone');
      return;
    }

    const cpfClean = formData.cpf.replace(/\D/g, '');
    if (cpfClean && cpfClean.length !== 11 && cpfClean.length !== 14) {
      toast.error('CPF/CNPJ inválido (deve ter 11 ou 14 dígitos)');
      return;
    }

    try {
      setLoading(true);
      const data = {
        nome_completo: formData.nome_completo,
        cpf: cpfClean || null,
        endereco: formData.endereco || null,
        bloco: formData.bloco || null,
        complemento: formData.complemento || null,
        area_total: formData.area_total || null,
        area_construida: formData.area_construida || null,
        telefone: formData.telefone.replace(/\D/g, ''),
        data_nascimento: formData.data_nascimento || null,
        parceiro_id: formData.parceiro_id || null,
        estado: formData.estado || null,
        cidade: formData.cidade || null,
        usuario_gov: formData.usuario_gov || null,
        senha_gov: formData.senha_gov || null,
        usuarios_gov: (formData.usuarios_gov || []).filter(u => (u.login || u.senha || u.label)),
        banco: formData.banco || null,
        agencia: formData.agencia || null,
        conta: formData.conta || null,
      };

      if (isEditing) {
        await clientsAPI.update(id, data);
        toast.success('Cliente atualizado com sucesso');
      } else {
        await clientsAPI.create(data);
        toast.success('Cliente cadastrado com sucesso');
      }
      navigate('/clientes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{isEditing ? 'Editar Cliente' : 'Novo Cliente'}</h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize os dados do cliente' : 'Cadastre um novo cliente no sistema'}
          </p>
        </div>
        <div className="flex gap-2">
          {isEditing && isAdmin && (
            <Button variant="outline" onClick={openTransferDialog} className="gap-2" data-testid="transfer-btn">
              <UserPlus className="w-4 h-4" />
              Transferir
            </Button>
          )}
          {isEditing && formData.telefone && (
            <Button variant="outline" onClick={openWhatsApp} className="gap-2" data-testid="whatsapp-btn">
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </Button>
          )}
        </div>
      </div>

      {isEditing && formData.criado_por_nome && (
        <div className="text-sm text-muted-foreground">
          Responsável: <span className="font-medium">{formData.criado_por_nome}</span>
        </div>
      )}

      {isEditing ? (
        <Tabs defaultValue="dados" className="w-full">
          <TabsList className={`grid w-full ${whatsappAtivo ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="dados">Dados</TabsTrigger>
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
            <TabsTrigger value="projetos">Projetos</TabsTrigger>
            {whatsappAtivo && <TabsTrigger value="whatsapp" data-testid="cliente-aba-whatsapp">WhatsApp</TabsTrigger>}
          </TabsList>

          <TabsContent value="dados">
            <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Dados do Cliente
          </CardTitle>
          <CardDescription>
            Campos com * são obrigatórios
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 space-y-2">
                <Label htmlFor="nome">Nome Completo *</Label>
                <Input
                  id="nome"
                  placeholder="Digite o nome completo"
                  value={formData.nome_completo}
                  onChange={(e) => handleChange('nome_completo', e.target.value)}
                  data-testid="input-nome"
                  className="text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF/CNPJ *</Label>
                <Input
                  id="cpf"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  value={formData.cpf}
                  onChange={(e) => handleChange('cpf', e.target.value)}
                  maxLength={18}
                  data-testid="input-cpf"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  maxLength={15}
                  data-testid="input-telefone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nascimento">Data de Nascimento</Label>
                <Input
                  id="nascimento"
                  type="date"
                  value={formData.data_nascimento}
                  onChange={(e) => handleChange('data_nascimento', e.target.value)}
                  data-testid="input-nascimento"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="parceiro">Indicação (Parceiro)</Label>
                <Select
                  value={formData.parceiro_id}
                  onValueChange={(v) => handleChange('parceiro_id', v)}
                >
                  <SelectTrigger data-testid="select-parceiro">
                    <SelectValue placeholder="Selecione um parceiro (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estado">Estado</Label>
                <Select
                  value={formData.estado}
                  onValueChange={(v) => handleChange('estado', v)}
                >
                  <SelectTrigger data-testid="select-estado">
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione...</SelectItem>
                    {estados.map((e) => (
                      <SelectItem key={e.sigla} value={e.sigla}>
                        {e.sigla} - {e.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade</Label>
                <Input
                  id="cidade"
                  placeholder="Digite a cidade"
                  value={formData.cidade}
                  onChange={(e) => handleChange('cidade', e.target.value.toUpperCase())}
                  data-testid="input-cidade"
                />
              </div>

              <div className="md:col-span-3 space-y-2">
                <Label htmlFor="endereco">Endereço</Label>
                <Input
                  id="endereco"
                  placeholder="Rua, número, bairro, cidade - UF (opcional)"
                  value={formData.endereco}
                  onChange={(e) => handleChange('endereco', e.target.value)}
                  data-testid="input-endereco"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bloco">Lote</Label>
                <Input
                  id="bloco"
                  placeholder="Lote (opcional)"
                  value={formData.bloco}
                  onChange={(e) => handleChange('bloco', e.target.value)}
                  data-testid="input-bloco"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="complemento">Rota</Label>
                <Input
                  id="complemento"
                  placeholder="Rota (opcional)"
                  value={formData.complemento}
                  onChange={(e) => handleChange('complemento', e.target.value)}
                  data-testid="input-complemento"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="area_total">Área Total</Label>
                <Input
                  id="area_total"
                  placeholder="Ex: 100 ha"
                  value={formData.area_total}
                  onChange={(e) => handleChange('area_total', e.target.value)}
                  data-testid="input-area-total"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="area_construida">Área Produtiva</Label>
                <Input
                  id="area_construida"
                  placeholder="Ex: 50 ha"
                  value={formData.area_construida}
                  onChange={(e) => handleChange('area_construida', e.target.value)}
                  data-testid="input-area-construida"
                />
              </div>

              {/* Dados Bancários */}
              <div className="md:col-span-3 pt-4 border-t">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Dados Bancários</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="banco">Banco</Label>
                <Select
                  value={formData.banco || 'none'}
                  onValueChange={(v) => handleChange('banco', v === 'none' ? '' : v)}
                >
                  <SelectTrigger data-testid="select-banco">
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione...</SelectItem>
                    {instituicoes.map((i) => (
                      <SelectItem key={i.id} value={i.nome}>{i.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="agencia">Agência</Label>
                <Input
                  id="agencia"
                  placeholder="Ex: 1234"
                  value={formData.agencia}
                  onChange={(e) => handleChange('agencia', e.target.value)}
                  data-testid="input-agencia"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="conta">Conta</Label>
                <Input
                  id="conta"
                  placeholder="Ex: 12345-6"
                  value={formData.conta}
                  onChange={(e) => handleChange('conta', e.target.value)}
                  data-testid="input-conta"
                />
              </div>

              {/* Dados GOV.BR — v1.6: agora suporta MÚLTIPLOS usuários */}
              <div className="md:col-span-3 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">Acesso GOV.BR</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const atuais = formData.usuarios_gov || [];
                      handleChange('usuarios_gov', [...atuais, { login: '', senha: '', label: '' }]);
                    }}
                    data-testid="btn-add-gov"
                    className="h-7 gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> Adicionar mais um usuário GOV
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="usuario_gov">Usuário GOV (principal)</Label>
                <Input
                  id="usuario_gov"
                  placeholder="CPF ou email do GOV.BR"
                  value={formData.usuario_gov}
                  onChange={(e) => handleChange('usuario_gov', e.target.value)}
                  data-testid="input-usuario-gov"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="senha_gov">Senha GOV (principal)</Label>
                <div className="relative">
                  <Input
                    id="senha_gov"
                    type={showSenhaGov ? 'text' : 'password'}
                    placeholder="Senha do GOV.BR"
                    value={formData.senha_gov}
                    onChange={(e) => handleChange('senha_gov', e.target.value)}
                    data-testid="input-senha-gov"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowSenhaGov(!showSenhaGov)}
                    data-testid="toggle-senha-gov"
                  >
                    {showSenhaGov ? (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div></div>

              {/* v1.6: usuários GOV adicionais */}
              {(formData.usuarios_gov || []).map((u, idx) => (
                <React.Fragment key={idx}>
                  <div className="md:col-span-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Usuário GOV adicional #{idx + 1}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const arr = [...(formData.usuarios_gov || [])];
                          arr.splice(idx, 1);
                          handleChange('usuarios_gov', arr);
                        }}
                        data-testid={`btn-remove-gov-${idx}`}
                        className="h-6 text-xs text-rose-600 hover:bg-rose-50"
                      >
                        Remover
                      </Button>
                    </div>
                    <Input
                      placeholder="Rótulo (ex: Cônjuge, Sócio, Procurador...)"
                      value={u.label || ''}
                      onChange={(e) => {
                        const arr = [...(formData.usuarios_gov || [])];
                        arr[idx] = { ...arr[idx], label: e.target.value };
                        handleChange('usuarios_gov', arr);
                      }}
                      data-testid={`input-gov-label-${idx}`}
                      className="mb-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Usuário GOV</Label>
                    <Input
                      placeholder="CPF ou email"
                      value={u.login || ''}
                      onChange={(e) => {
                        const arr = [...(formData.usuarios_gov || [])];
                        arr[idx] = { ...arr[idx], login: e.target.value };
                        handleChange('usuarios_gov', arr);
                      }}
                      data-testid={`input-gov-login-${idx}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Senha GOV</Label>
                    <Input
                      type="text"
                      placeholder="Senha"
                      value={u.senha || ''}
                      onChange={(e) => {
                        const arr = [...(formData.usuarios_gov || [])];
                        arr[idx] = { ...arr[idx], senha: e.target.value };
                        handleChange('usuarios_gov', arr);
                      }}
                      data-testid={`input-gov-senha-${idx}`}
                    />
                  </div>
                  <div></div>
                </React.Fragment>
              ))}
            </div>

            <div className="flex justify-end gap-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/clientes')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-client-btn">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
          </TabsContent>

          <TabsContent value="documentos">
            <div className="space-y-6">
              {/* Caixa de Documentos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Documentos
                  </CardTitle>
                  <CardDescription>
                    Documentos pessoais e gerais do cliente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload */}
                  <div className="space-y-2">
                    <Label>Adicionar documentos (máx. {limiteUpload.mb} MB cada)</Label>
                    <Input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploading}
                      data-testid="client-file-upload"
                    />
                    {uploading && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="animate-spin">⏳</span>
                        Enviando...
                      </p>
                    )}
                  </div>

                  {/* Lista de arquivos */}
                  <ScrollArea className="h-80 border rounded-lg">
                    {files.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
                        <FileText className="w-12 h-12 mb-3 opacity-30" />
                        <p>Nenhum documento</p>
                      </div>
                    ) : (
                      <div className="p-3 space-y-2">
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 gap-2"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FileText className="w-5 h-5 flex-shrink-0 text-primary" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium break-all">{file.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(file.size / 1024).toFixed(1)} KB • {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('pt-BR') : '-'} • {file.uploaded_by_name || 'Sistema'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDownload(file.name)}
                                data-testid={`download-file-${idx}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleDeleteFile(file.name)}
                                data-testid={`delete-file-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Caixa de Projetos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5" />
                    Projetos
                  </CardTitle>
                  <CardDescription>
                    Arquivos dos projetos do cliente (sincronizado com Dashboard)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload - só mostra se tiver projetos */}
                  {clientProjects.length > 0 ? (
                    <div className="space-y-2">
                      <Label>Adicionar arquivos de projeto (máx. {limiteUpload.mb} MB cada)</Label>
                      {clientProjects.length > 1 && (
                        <Select
                          value={selectedProjectForUpload}
                          onValueChange={setSelectedProjectForUpload}
                        >
                          <SelectTrigger className="mb-2">
                            <SelectValue placeholder="Selecione o projeto" />
                          </SelectTrigger>
                          <SelectContent>
                            {clientProjects.map((proj) => (
                              <SelectItem key={proj.id} value={proj.id}>
                                {proj.tipo_projeto_nome || 'Projeto'} - {proj.instituicao_nome || 'Sem instituição'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        type="file"
                        multiple
                        onChange={handleProjectFileUpload}
                        disabled={uploadingProject || !selectedProjectForUpload}
                        data-testid="project-file-upload"
                      />
                      {uploadingProject && (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <span className="animate-spin">⏳</span>
                          Enviando...
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      📌 Crie um projeto pelo Dashboard para adicionar arquivos
                    </p>
                  )}

                  {/* Lista de arquivos */}
                  <ScrollArea className="h-80 border rounded-lg">
                    {projectFiles.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
                        <FolderOpen className="w-12 h-12 mb-3 opacity-30" />
                        <p>Nenhum arquivo de projeto</p>
                        {clientProjects.length === 0 && (
                          <p className="text-xs mt-1">Crie um projeto primeiro</p>
                        )}
                      </div>
                    ) : (
                      <div className="p-3 space-y-2">
                        {projectFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 gap-2"
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FolderOpen className="w-5 h-5 flex-shrink-0 text-primary" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium break-all">{file.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {file.projeto_nome && <span className="font-medium text-primary">[{file.projeto_nome}]</span>} {(file.size / 1024).toFixed(1)} KB • {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('pt-BR') : '-'} • {file.uploaded_by_name || 'Sistema'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleProjectDownload(file.name, file.projeto_id)}
                                data-testid={`download-project-file-${idx}`}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleDeleteProjectFile(file.name, file.projeto_id)}
                                data-testid={`delete-project-file-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {whatsappAtivo && (
            <TabsContent value="whatsapp">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">Atendimentos no WhatsApp</CardTitle>
                </CardHeader>
                <CardContent><AtendimentosCliente clienteId={id} /></CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="projetos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" />
                  Projetos do Cliente
                </CardTitle>
                <CardDescription>
                  Histórico de todos os projetos deste cliente
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingProjects ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : clientProjects.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhum projeto encontrado para este cliente</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Instituição</TableHead>
                        <TableHead>Valor Crédito</TableHead>
                        <TableHead>Etapa Atual</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Data Início</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientProjects.map((proj) => (
                        <React.Fragment key={proj.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setExpandedClientProject(expandedClientProject === proj.id ? null : proj.id)}
                          data-testid={`client-project-row-${proj.id}`}
                        >
                          <TableCell>
                            {expandedClientProject === proj.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </TableCell>
                          <TableCell className="font-medium">{proj.tipo_projeto}</TableCell>
                          <TableCell>{proj.instituicao_financeira_nome || '-'}</TableCell>
                          <TableCell>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(proj.valor_credito || 0)}
                          </TableCell>
                          <TableCell>{proj.etapa_atual_nome}</TableCell>
                          <TableCell>
                            {proj.status === 'em_andamento' && (
                              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                <Clock className="w-3 h-3 mr-1" />
                                Em Andamento
                              </Badge>
                            )}
                            {proj.status === 'arquivado' && (
                              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Finalizado
                              </Badge>
                            )}
                            {proj.status === 'desistido' && (
                              <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
                                <XCircle className="w-3 h-3 mr-1" />
                                Desistido
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {proj.data_inicio ? new Date(proj.data_inicio).toLocaleDateString('pt-BR') : '-'}
                          </TableCell>
                          <TableCell>
                            {proj.status === 'em_andamento' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); navigate(`/projetos?project=${proj.id}`); }}
                              >
                                Ver Projeto
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedClientProject === proj.id && (
                          <TableRow>
                            <TableCell colSpan={8} className="p-0 bg-muted/30">
                              <div className="p-5 space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  {proj.numero_interno && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Nº Interno</p><p className="font-mono font-medium">{proj.numero_interno}</p></div>}
                                  {proj.numero_contrato && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Nº Contrato</p><p className="font-medium">{proj.numero_contrato}</p></div>}
                                  {proj.valor_servico > 0 && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Valor Serviço</p><p className="font-medium text-emerald-600">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(proj.valor_servico)}</p></div>}
                                  {proj.parceiro && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Parceiro</p><p className="font-medium">{proj.parceiro}</p></div>}
                                  {proj.usuario_responsavel_nome && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Responsável</p><p className="font-medium">{proj.usuario_responsavel_nome}</p></div>}
                                  {proj.data_arquivamento && <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Arquivado em</p><p className="font-medium">{new Date(proj.data_arquivamento).toLocaleDateString('pt-BR')}</p></div>}
                                </div>

                                {proj.dados_complementares && Object.keys(proj.dados_complementares).length > 0 && (
                                  <div className="p-3 rounded-lg border bg-card">
                                    <h5 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Dados Complementares</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                      {Object.entries(proj.dados_complementares).flatMap(([ek, vals]) => {
                                        if (!vals || typeof vals !== 'object') return [];
                                        return Object.entries(vals)
                                          .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                                          .map(([k, v]) => (
                                            <div key={`${ek}-${k}`}>
                                              <p className="text-[10px] text-muted-foreground">{k.replace(/_/g, ' ')}</p>
                                              <p className="font-medium break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</p>
                                            </div>
                                          ));
                                      })}
                                    </div>
                                  </div>
                                )}

                                {proj.marcas_valores && Object.keys(proj.marcas_valores).length > 0 && (
                                  <div className="p-3 rounded-lg border bg-blue-50/30 dark:bg-blue-950/20">
                                    <h5 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Marcas</h5>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                      {Object.entries(proj.marcas_valores)
                                        .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                                        .map(([k, v]) => (
                                          <div key={k}>
                                            <p className="text-[10px] text-muted-foreground">{k.replace(/_/g, ' ')}</p>
                                            <p className="font-medium break-words">{String(v)}</p>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {proj.status === 'desistido' && proj.motivo_desistencia && (
                                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20">
                                    <h5 className="text-[11px] uppercase tracking-wider font-semibold text-red-700 dark:text-red-400 mb-1">Motivo de Desistência</h5>
                                    <p className="text-sm whitespace-pre-wrap">{proj.motivo_desistencia}</p>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informações do Cliente
            </CardTitle>
            <CardDescription>Campos com * são obrigatórios</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Dados Pessoais */}
                <div className="md:col-span-3 space-y-2">
                  <Label htmlFor="nome_completo">Nome Completo *</Label>
                  <Input
                    id="nome_completo"
                    placeholder="Nome do cliente"
                    value={formData.nome_completo}
                    onChange={(e) => handleChange('nome_completo', e.target.value.toUpperCase())}
                    required
                    data-testid="input-nome"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF/CNPJ</Label>
                  <Input
                    id="cpf"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    value={formData.cpf}
                    onChange={(e) => handleChange('cpf', formatCPFCNPJ(e.target.value))}
                    maxLength={18}
                    data-testid="input-cpf"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone *</Label>
                  <Input
                    id="telefone"
                    placeholder="(00) 00000-0000"
                    value={formData.telefone}
                    onChange={(e) => handleChange('telefone', formatPhone(e.target.value))}
                    maxLength={15}
                    required
                    data-testid="input-telefone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                  <Input
                    id="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => handleChange('data_nascimento', e.target.value)}
                    data-testid="input-nascimento"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parceiro_id">Parceiro (Indicação)</Label>
                  <Select value={formData.parceiro_id || 'none'} onValueChange={(v) => handleChange('parceiro_id', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="select-parceiro">
                      <SelectValue placeholder="Selecione um parceiro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {partners.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Select
                    value={formData.estado || 'none'}
                    onValueChange={(v) => {
                      handleChange('estado', v === 'none' ? '' : v);
                    }}
                  >
                    <SelectTrigger data-testid="select-estado">
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione</SelectItem>
                      {estados.map((e) => (
                        <SelectItem key={e.sigla} value={e.sigla}>{e.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    placeholder="Digite a cidade"
                    value={formData.cidade}
                    onChange={(e) => handleChange('cidade', e.target.value.toUpperCase())}
                    data-testid="input-cidade"
                  />
                </div>

                {/* Endereço */}
                <div className="md:col-span-3 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Endereço</h3>
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="endereco">Endereço</Label>
                  <Input
                    id="endereco"
                    placeholder="Rua, número, bairro"
                    value={formData.endereco}
                    onChange={(e) => handleChange('endereco', e.target.value)}
                    data-testid="input-endereco"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bloco">Lote</Label>
                  <Input
                    id="bloco"
                    placeholder="Ex: Lote A"
                    value={formData.bloco}
                    onChange={(e) => handleChange('bloco', e.target.value)}
                    data-testid="input-bloco"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="complemento">Rota</Label>
                  <Input
                    id="complemento"
                    placeholder="Rota (opcional)"
                    value={formData.complemento}
                    onChange={(e) => handleChange('complemento', e.target.value)}
                    data-testid="input-complemento"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="area_total">Área Total</Label>
                  <Input
                    id="area_total"
                    placeholder="Ex: 100 ha"
                    value={formData.area_total}
                    onChange={(e) => handleChange('area_total', e.target.value)}
                    data-testid="input-area-total"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="area_construida">Área Produtiva</Label>
                  <Input
                    id="area_construida"
                    placeholder="Ex: 50 ha"
                    value={formData.area_construida}
                    onChange={(e) => handleChange('area_construida', e.target.value)}
                    data-testid="input-area-construida"
                  />
                </div>

                {/* Dados Bancários */}
                <div className="md:col-span-3 pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Dados Bancários</h3>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="banco">Banco</Label>
                  <Select
                    value={formData.banco || 'none'}
                    onValueChange={(v) => handleChange('banco', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger data-testid="select-banco">
                      <SelectValue placeholder="Selecione o banco" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione...</SelectItem>
                      {instituicoes.map((i) => (
                        <SelectItem key={i.id} value={i.nome}>{i.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agencia">Agência</Label>
                  <Input
                    id="agencia"
                    placeholder="Ex: 1234"
                    value={formData.agencia}
                    onChange={(e) => handleChange('agencia', e.target.value)}
                    data-testid="input-agencia"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="conta">Conta</Label>
                  <Input
                    id="conta"
                    placeholder="Ex: 12345-6"
                    value={formData.conta}
                    onChange={(e) => handleChange('conta', e.target.value)}
                    data-testid="input-conta"
                  />
                </div>

                {/* GOV.BR — v1.6: agora suporta MÚLTIPLOS usuários */}
                <div className="md:col-span-3 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Acesso GOV.BR</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const atuais = formData.usuarios_gov || [];
                        handleChange('usuarios_gov', [...atuais, { login: '', senha: '', label: '' }]);
                      }}
                      data-testid="btn-add-gov"
                      className="h-7 gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Adicionar mais um usuário GOV
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="usuario_gov">Usuário GOV (principal)</Label>
                  <Input
                    id="usuario_gov"
                    placeholder="CPF ou email do GOV.BR"
                    value={formData.usuario_gov}
                    onChange={(e) => handleChange('usuario_gov', e.target.value)}
                    data-testid="input-usuario-gov"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="senha_gov">Senha GOV (principal)</Label>
                  <Input
                    id="senha_gov"
                    type="password"
                    placeholder="Senha do GOV.BR"
                    value={formData.senha_gov}
                    onChange={(e) => handleChange('senha_gov', e.target.value)}
                    data-testid="input-senha-gov"
                  />
                </div>

                {/* v1.6: usuários GOV adicionais */}
                {(formData.usuarios_gov || []).map((u, idx) => (
                  <React.Fragment key={idx}>
                    <div className="md:col-span-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Usuário GOV adicional #{idx + 1}</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const arr = [...(formData.usuarios_gov || [])];
                            arr.splice(idx, 1);
                            handleChange('usuarios_gov', arr);
                          }}
                          data-testid={`btn-remove-gov-${idx}`}
                          className="h-6 text-xs text-rose-600 hover:bg-rose-50"
                        >
                          Remover
                        </Button>
                      </div>
                      <Input
                        placeholder="Rótulo (ex: Cônjuge, Sócio, Procurador...)"
                        value={u.label || ''}
                        onChange={(e) => {
                          const arr = [...(formData.usuarios_gov || [])];
                          arr[idx] = { ...arr[idx], label: e.target.value };
                          handleChange('usuarios_gov', arr);
                        }}
                        data-testid={`input-gov-label-${idx}`}
                        className="mb-2"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Usuário GOV</Label>
                      <Input
                        placeholder="CPF ou email"
                        value={u.login || ''}
                        onChange={(e) => {
                          const arr = [...(formData.usuarios_gov || [])];
                          arr[idx] = { ...arr[idx], login: e.target.value };
                          handleChange('usuarios_gov', arr);
                        }}
                        data-testid={`input-gov-login-${idx}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Senha GOV</Label>
                      <Input
                        type="text"
                        placeholder="Senha"
                        value={u.senha || ''}
                        onChange={(e) => {
                          const arr = [...(formData.usuarios_gov || [])];
                          arr[idx] = { ...arr[idx], senha: e.target.value };
                          handleChange('usuarios_gov', arr);
                        }}
                        data-testid={`input-gov-senha-${idx}`}
                      />
                    </div>
                    <div></div>
                  </React.Fragment>
                ))}
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/clientes')} disabled={loading}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="save-client-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Transferência */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir Cliente</DialogTitle>
            <DialogDescription>
              Selecione o usuário para quem deseja transferir este cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Novo Responsável</Label>
              <Select value={selectedUsuario} onValueChange={setSelectedUsuario}>
                <SelectTrigger data-testid="select-novo-usuario">
                  <SelectValue placeholder="Selecione um usuário" />
                </SelectTrigger>
                <SelectContent>
                  {filtrarUsuariosAtribuicao(usuarios).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.nome} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleTransferir} disabled={transferindo}>
              {transferindo ? 'Transferindo...' : 'Transferir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientForm;
